import type { ComponentChildren } from 'preact';
import { pickLocale, type CheckoutBlockDto } from './api.js';
import { MarkdownBlock } from './blocks/MarkdownBlock.js';
import { applyScopeTemplate } from './util/template.js';

/**
 * Renders a single CheckoutBlock. Decorative blocks are self-contained;
 * anchor blocks (lookup-form, method-tiles, consents, summary, pay-button)
 * resolve through the `slots` map so the parent injects the right
 * interactive component.
 *
 * The optional `scope` enables `{{ scope.foo }}` token substitution inside
 * `markdown`, `paragraph`, `heading`, and `callout` blocks — operators can
 * inject runtime values (bedrock-generated tips, integration outputs) into
 * their copy without escaping into a custom block kind.
 */
export function Block(props: {
  block: CheckoutBlockDto;
  locale: string;
  slots: Partial<Record<CheckoutBlockDto['type'], ComponentChildren>>;
  scope?: Record<string, unknown>;
}) {
  const { block, locale, slots, scope } = props;
  const tpl = (s: string): string => (scope ? applyScopeTemplate(s, scope) : s);
  switch (block.type) {
    case 'heading': {
      const Tag = block.level;
      const align = block.align ?? 'left';
      const cls =
        block.level === 'h1'
          ? 'text-h1 font-semibold tracking-tight'
          : block.level === 'h2'
            ? 'text-h2 font-semibold tracking-tight'
            : 'text-h3 font-semibold';
      return <Tag class={`${cls} text-${align}`}>{tpl(pickLocale(block.text, locale))}</Tag>;
    }
    case 'paragraph': {
      const sizeCls = block.size === 'lg' ? 'text-body-lg' : block.size === 'sm' ? 'text-[11px]' : 'text-body-md';
      return (
        <p class={`${sizeCls} text-on-surface-variant`}>{tpl(pickLocale(block.text, locale))}</p>
      );
    }
    case 'image': {
      const align = block.align ?? 'left';
      const maxW = block.maxWidth ? `${block.maxWidth}px` : '100%';
      return (
        <div class={`text-${align}`}>
          <img
            src={block.src}
            alt={pickLocale(block.alt, locale)}
            style={{ maxWidth: maxW, display: 'inline-block' }}
            class="rounded"
          />
        </div>
      );
    }
    case 'divider':
      return <hr class="border-t border-outline-variant my-2" />;
    case 'spacer':
      return (
        <div
          aria-hidden="true"
          style={{
            height: block.size === 'lg' ? '32px' : block.size === 'md' ? '16px' : '8px',
          }}
        />
      );
    case 'callout': {
      const toneCls =
        block.tone === 'warning'
          ? 'border-warning bg-warning-container/30 text-on-warning-container'
          : block.tone === 'success'
            ? 'border-success bg-success-container/30 text-on-success-container'
            : block.tone === 'danger'
              ? 'border-error bg-error-container/30 text-on-error-container'
              : 'border-primary bg-primary-container/20 text-on-primary-container';
      return (
        <div class={`flex items-start gap-2 p-3 border-l-4 rounded-r ${toneCls}`}>
          {block.icon && (
            <span class="material-symbols-outlined" style={{ fontSize: '18px' }} aria-hidden="true">
              {block.icon}
            </span>
          )}
          {block.scopeMarkdown ? (
            // Rich callout (e.g. the LLM weather tip): render text as sanitized
            // markdown so the value's **bold** / --- format. Sanitizer gates XSS.
            <div class="text-body-sm leading-snug">
              <MarkdownBlock source={block.text} locale={locale} {...(scope ? { scope } : {})} scopeMarkdown />
            </div>
          ) : (
            <p class="text-body-sm leading-snug">{tpl(pickLocale(block.text, locale))}</p>
          )}
        </div>
      );
    }
    case 'lookup-form':
    case 'method-tiles':
    case 'consents':
    case 'summary':
    case 'pay-button':
      // Anchor blocks — parent supplied the rendered slot.
      return <>{slots[block.type] ?? null}</>;
    // === Sprint F — operator-authored render-only blocks ===
    case 'form-fields':
      // Anchor-style: parent passes a configured <DynamicForm> via slots.
      // When no slot is supplied (runner didn't wire it), render nothing
      // so the operator-authored block degrades cleanly rather than
      // crashing the page.
      return <>{slots['form-fields'] ?? null}</>;
    case 'markdown':
      return (
        <MarkdownBlock
          source={block.source}
          locale={locale}
          {...(scope ? { scope } : {})}
          {...(block.scopeMarkdown ? { scopeMarkdown: true } : {})}
        />
      );
    case 'risk-badge':
      // The runner has the risk session in scope, not BlockRenderer. The
      // runner picks the slice (decision / score / narration / all)
      // matching `block.show` and supplies the rendered badge via the
      // slot. No session → no slot → nothing renders (the operator's
      // block degrades cleanly when risk is disabled).
      return <>{slots['risk-badge'] ?? null}</>;
    case 'progress-stepper': {
      // Two paths:
      //   - `stepsFromFlow` (default true): parent supplies the
      //     rendered stepper via the slot — the runner has the flow
      //     graph and current-node id in scope.
      //   - `stepsFromFlow === false`: the operator embedded an
      //     explicit `steps` list; self-render a horizontal pill list
      //     with `currentStepId` highlighted.
      if (block.stepsFromFlow !== false) {
        return <>{slots['progress-stepper'] ?? null}</>;
      }
      const steps = block.steps ?? [];
      return (
        <ol class="flex flex-wrap gap-2" aria-label="Progress">
          {steps.map((s) => {
            const active = s.id === block.currentStepId;
            const cls = active
              ? 'bg-primary text-on-primary'
              : 'bg-surface-container text-on-surface-variant';
            return (
              <li
                key={s.id}
                class={`px-3 py-1 rounded-full text-body-sm font-medium ${cls}`}
                aria-current={active ? 'step' : undefined}
              >
                {pickLocale(s.label, locale)}
              </li>
            );
          })}
        </ol>
      );
    }
    default:
      return null;
  }
}

/**
 * Renders an ordered list of blocks. The parent passes the form-anchor
 * components in `slots`; this component only knows about layout.
 */
export function BlockRenderer(props: {
  blocks: CheckoutBlockDto[];
  locale: string;
  slots: Partial<Record<CheckoutBlockDto['type'], ComponentChildren>>;
  /**
   * Optional runtime scope for `{{ scope.foo }}` substitution inside
   * `markdown` / `paragraph` / `heading` / `callout` text. Passing scope
   * is opt-in so legacy block lists rendered outside of a flow context
   * (the strategy-editor live preview, for example) keep their literal
   * `{{ token }}` text rather than rendering an empty string.
   */
  scope?: Record<string, unknown>;
}) {
  return (
    <div class="space-y-4">
      {props.blocks.map((b) => (
        <Block
          key={b.id}
          block={b}
          locale={props.locale}
          slots={props.slots}
          {...(props.scope ? { scope: props.scope } : {})}
        />
      ))}
    </div>
  );
}
